import { PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PageNavBar } from 'components/PageNavBar'
import { accountTypes } from 'config/user.type.constants'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { LoanType, loanTypeMap } from 'reducers/loanDetail.reducer'
import { getAdminConfig, setAdminConfig } from 'services'
import { ButtonGroup, Toggle, ToggleButton } from 'stories/components'
import { capitalizeLettersSeparatedBySlash, prompt } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { workflowRestrictions } from './constants'
import { IWorkflowConfig, TMultiWorkflowConfig, TWorkflowConfig } from './type'

export const SingleWorkflow = ({
  type,
  data: _data,
  setData,
  setLoading,
}: {
  type: LoanType
  data: TMultiWorkflowConfig
  setData: Function
  setLoading: Function
}) => {
  const data = _data[type]
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])

  const onChange = async (titleKey: string, key: string, subKey: keyof IWorkflowConfig, value: any) => {
    const temp = cloneDeep(data)
    ;(temp[titleKey][key] as any)[subKey] = value
    if (subKey == 'enabled' && Object.keys(temp[titleKey][key]).includes('isActive'))
      temp[titleKey][key]['isActive'] = value

    setData(temp)
    setLoading(true)
    await setAdminConfig(`workflows/${type}`, temp)
    setLoading(false)
  }

  const onChangeTitle = async (titleKey: string, key: string) => {
    const originalTitle = data[titleKey][key].title || capitalizeLettersSeparatedBySlash(key)
    const newValue = await prompt(`Input title for Action - ${capitalizeLettersSeparatedBySlash(key)}`, {
      value: originalTitle,
    })
    if (!newValue) return
    onChange(titleKey, key, 'title', newValue)
  }

  return (
    <>
      {Object.keys(data).map((titleKey) => {
        const item = data[titleKey]
        const defaultItem = _data[LoanType.DEFAULT]?.[titleKey]
        return (
          <React.Fragment key={titleKey}>
            <div className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center mb-8`}>
              - {capitalizeLettersSeparatedBySlash(titleKey)}
            </div>
            <PageNavBar
              titles={Object.keys(item).map(
                (key) => defaultItem?.[key]?.title || capitalizeLettersSeparatedBySlash(key),
              )}
              activeIndex={-1}
              activeIndexs={Object.keys(item)
                .map((key, index: number) => {
                  return { key, index }
                })
                .filter(({ key }) => item[key].enabled)
                .map(({ index }) => index)}
              onMove={(index: number) => {
                const key = Object.keys(item)[index]
                onChange(titleKey, key, 'enabled', !item[key].enabled)
              }}
              isAllClickable
            />
            {Object.keys(item).map((key) => (
              <div className="mb-4" key={key}>
                <RenderInput
                  Key={key}
                  key={key}
                  input={{
                    inputType: 'section',
                    title: defaultItem?.[key]?.title || capitalizeLettersSeparatedBySlash(key),
                    additionalElements: (
                      <div className="flex gap-2 items-center">
                        <Toggle
                          id={key}
                          value={item[key].enabled}
                          onChange={(value) => onChange(titleKey, key, 'enabled', value)}
                        />
                        {type == LoanType.DEFAULT && (
                          <span className="btn-icon" onClick={() => onChangeTitle(titleKey, key)}>
                            <PencilSquareIcon className="w-4 h-4" />
                          </span>
                        )}
                      </div>
                    ),
                  }}
                  onChange={() => {}}
                />
                <div className="flex flex-wrap gap-2 items-center justify-between mt-4 py-2">
                  <div className="max-w-[400px] w-full">
                    <RenderInput
                      Key={key}
                      key={key}
                      input={{
                        inputType: 'multiselect',
                        title: 'Visible For',
                        options: { self_creator: '[Loan] Creator', ...accountTypes },
                        value: item[key].visibleAccountTypes || [],
                        defaultSelected: false,
                      }}
                      onChange={(key: string, value: any) => onChange(titleKey, key, 'visibleAccountTypes', value)}
                    />
                  </div>
                  <div className="max-w-[400px] w-full">
                    <RenderInput
                      Key={key}
                      key={key}
                      input={{
                        inputType: 'multiselect',
                        title: 'Disabled At',
                        options: loanGlobalStatus,
                        value: item[key].disabledAtStatus || [],
                        sort: true,
                        defaultSelected: false,
                      }}
                      onChange={(key: string, value: any) => onChange(titleKey, key, 'disabledAtStatus', value)}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <p>Active Condition</p>
                    <ToggleButton
                      id={`${key}-isActive`}
                      value={item[key]['isActive']}
                      onChange={(value) => onChange(titleKey, key, 'isActive', value)}
                      disabled={!item[key]['enabled']}
                    />
                  </div>
                </div>

                <div>
                  <RenderInput
                    Key={key}
                    key={key}
                    input={{
                      inputType: 'multiselect',
                      title: 'Conditions',
                      options: workflowRestrictions,
                      value: item[key].restrictions || [],
                      defaultSelected: false,
                      prefix: 'alphabet',
                    }}
                    onChange={(key: string, value: any) => onChange(titleKey, key, 'restrictions', value)}
                  />
                </div>
              </div>
            ))}
          </React.Fragment>
        )
      })}
    </>
  )
}

export const Workflows = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<TMultiWorkflowConfig>()
  const [loanType, setLoanType] = useState(LoanType.DEFAULT)

  const initData = async () => {
    setLoading(true)
    const res = await getAdminConfig('workflows')
    setData(res)
    setLoading(false)
  }

  useEffect(() => {
    initData()
  }, [])

  const onUpdate = (item: TWorkflowConfig) => {
    if (!data) return

    const newData = cloneDeep(data)
    newData[loanType] = item
    setData(newData)
  }

  return (
    <div className="relative px-3 py-1.5">
      <LayoutLoading show={isLoading} />

      <div className="text-xl font-bold mb-4">2. Workflows</div>
      <ButtonGroup title={loanTypeMap} onChange={setLoanType} value={`${loanType}`} />
      {data && <SingleWorkflow type={loanType} data={data} setData={onUpdate} setLoading={setLoading} />}
    </div>
  )
}
